package org.example.project.yard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.ContentOffset
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginekartei.KarteiApi
import org.example.project.engineletter.LETTER_LAYOUT_QUALITAET_WE_BERICHT
import org.example.project.engineletter.RenderYardLetter
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.GlobalManager
import org.example.project.service.YardApi
import org.example.project.service.YardSettingsService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text

@Composable
fun WEWebKontrolleAuskunftScreen() {
    val scope = rememberCoroutineScope()
    var kontrollen by remember { mutableStateOf<List<All>>(emptyList()) }
    var lkwListe by remember { mutableStateOf<List<All>>(emptyList()) }
    var unternehmen by remember { mutableStateOf(All()) }
    var logoSrc by remember { mutableStateOf("") }
    var selectedKey by remember { mutableStateOf<String?>(null) }
    var previewKontrolleKey by remember { mutableStateOf<String?>(null) }
    var searchedKontrollen by remember { mutableStateOf<List<All>?>(emptyList()) }
    var searchedPositionen by remember { mutableStateOf<List<All>?>(emptyList()) }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("yard-kontrollen")
    )

    LaunchedEffect(refreshVersion) {
        searchedKontrollen = emptyList()
        searchedPositionen = emptyList()
        kontrollen = YardApi.loadWeWebKontrollen()
        lkwListe = YardApi.loadWeLkwListe()
        unternehmen = KarteiApi.loadUnternehmen() ?: All()
        logoSrc = YardSettingsService.logoForLayout(LETTER_LAYOUT_QUALITAET_WE_BERICHT)
    }

    val topHeaders = listOf(
        "LS Nummer",
        "LKW",
        "Auflieger",
        "Fahrer",
        "Bruch",
        "Überzählig",
        "Fehlmenge",
        "Positionen",
        "Erfasst am",
        "Erfasst um"
    )
    val selectedPositionen = remember(selectedKey, kontrollen) {
        selectedKey
            ?.let { key -> kontrollen.firstOrNull { it.weWebKontrolleKey() == key } }
            ?.kontrollPositionen
            .orEmpty()
    }
    val selectedKontrolle = remember(selectedKey, kontrollen) {
        selectedKey?.let { key -> kontrollen.firstOrNull { it.weWebKontrolleKey() == key } }
    }
    val previewKontrolle = remember(previewKontrolleKey, kontrollen) {
        previewKontrolleKey?.let { key -> kontrollen.firstOrNull { it.weWebKontrolleKey() == key } }
    }
    val previewAuftrag = remember(previewKontrolle, lkwListe) {
        previewKontrolle?.let { kontrolle -> lkwListe.firstOrNull { it.weWebKontrolleMatch(kontrolle) } ?: kontrolle }
    }

    TabularSplitScreen(
        screen1 = {
            TabularScreen(
                screenKey = "WEWebKontrolleAuskunft_LKW",
                rollenKey = "WEWebKontrolleAuskunftScreen",
                title = "WE Fehlerprotokoll",
                headers = topHeaders,
                models = (searchedKontrollen ?: kontrollen).map { it.weWebKontrolleWithStableId() },
                data = (searchedKontrollen ?: kontrollen).map { kontrolle ->
                    val key = kontrolle.weWebKontrolleKey()
                    key to mapWeWebKontrolleTopRow(kontrolle, lkwListe)
                },
                nonEditableCols = topHeaders.toSet(),
                editable = false,
                includeMetadata = true,
                useUserSettings = false,
                onRowClick = { rowId ->
                    selectedKey = rowId
                    searchedPositionen = emptyList()
                },
                onSearchWithFilters = { filters ->
                    scope.launch { searchedKontrollen = YardApi.searchWeWebKontrollen(filters, "kontrollen") }
                },
                extraNavButtons = listOf(
                    TabularToolbarButton(
                        label = "Preview",
                        icon = "images/vorschau.svg",
                        enabled = selectedKontrolle != null,
                        onClick = { previewKontrolleKey = selectedKontrolle?.weWebKontrolleKey() }
                    )
                ),
                onCommit = {}
            )
        },
        screen2 = {
            WEWebKontrollePositionenTable(
                selectedKontrolle, searchedPositionen ?: selectedPositionen,
                onSearch = { filters ->
                    scope.launch {
                        searchedPositionen = YardApi.searchWeWebKontrollen(filters, "positionen", selectedKey.orEmpty())
                    }
                }
            )
        },
        initialRatio = 0.48f
    )

    if (previewKontrolle != null) {
        WEWebKontrolleBerichtPreviewPopup(
            auftrag = previewAuftrag,
            kontrolle = previewKontrolle,
            unternehmen = unternehmen,
            logoSrc = logoSrc,
            onClose = { previewKontrolleKey = null }
        )
    }
}

@Composable
private fun WEWebKontrolleBerichtPreviewPopup(
    auftrag: All?,
    kontrolle: All?,
    unternehmen: All,
    logoSrc: String,
    onClose: () -> Unit
) {
    PopupEngineCard(
        windowKey = "we-web-kontrolle-bericht-preview",
        widthPx = 1280,
        heightPx = 760,
        minWidthPx = 900,
        minHeightPx = 560,
        initialX = 220.0,
        initialY = 100.0,
        contentInsetLeft = ContentOffset.LEFT.value.toDouble(),
        contentInsetTop = ContentOffset.TOP.value.toDouble(),
        contentInsetRight = ContentOffset.RIGHT.value.toDouble(),
        contentInsetBottom = ContentOffset.BOTTOM.value.toDouble(),
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({
            style {
                height(100.percent)
                minHeight(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
                padding(16.px)
                boxSizing("border-box")
                color(DashboardTheme.TEXT_LIGHT)
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    fontSize(18.px)
                    lineHeight(24.px)
                    textAlign("center")
                    flexShrink(0)
                }
            }) {
                Text("WE Fehlerprotokoll Preview")
            }
            Div({
                style {
                    flex(1)
                    minHeight(0.px)
                    overflow("auto")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.FlexStart)
                    padding(10.px)
                    boxSizing("border-box")
                }
            }) {
                Div({
                    style {
                        width(2450.px)
                        property("transform", "scale(0.68)")
                        property("transform-origin", "top center")
                    }
                }) {
                    RenderYardLetter(
                        layoutKey = LETTER_LAYOUT_QUALITAET_WE_BERICHT,
                        auftrag = auftrag,
                        vorgangslog = emptyList(),
                        unternehmen = unternehmen,
                        logoSrc = logoSrc,
                        webKontrolle = kontrolle,
                        sideBySidePreview = true
                    )
                }
            }
        }
    }
}

@Composable
private fun WEWebKontrollePositionenTable(
    kontrolle: All?,
    positionen: List<All>,
    onSearch: (Map<String, String>) -> Unit
) {
    val headers = listOf(
        "LS Nummer",
        "Position",
        "Typ",
        "NVE / Charge",
        "L-Code",
        "MHD",
        "Artikel Nummer",
        "Menge"
    )
    TabularScreen(
        screenKey = "WEWebKontrolleAuskunft_Positionen",
        rollenKey = "WEWebKontrolleAuskunftScreen",
        title = "Kontrollpositionen",
        headers = headers,
        models = positionen.mapIndexed { index, position -> position.weWebKontrollePositionWithStableId(kontrolle, index) },
        data = positionen.mapIndexed { index, position ->
            "${kontrolle?.weWebKontrolleKey().orEmpty()}:${index + 1}" to mapWeWebKontrollePositionRow(kontrolle, position, index)
        },
        nonEditableCols = headers.toSet(),
        editable = false,
        includeMetadata = true,
        useUserSettings = false,
        onSearchWithFilters = onSearch,
        onCommit = {}
    )
}

private fun mapWeWebKontrolleTopRow(kontrolle: All, lkwListe: List<All>): List<String> {
    val lkw = lkwListe.firstOrNull { it.weWebKontrolleMatch(kontrolle) }
    return listOf(
        kontrolle.lieferscheinnummer,
        lkw?.lkwkennzeichen.orEmpty(),
        lkw?.aufliegerkennzeichen.orEmpty(),
        lkw?.fahrername.orEmpty(),
        kontrolle.mobilWePalettenBruch.takeIf { it > 0 }?.toString() ?: "0",
        kontrolle.mobilWePalettenUeberzaehlig.takeIf { it > 0 }?.toString() ?: "0",
        kontrolle.mobilWePalettenFehlmenge.takeIf { it > 0 }?.toString() ?: "0",
        kontrolle.kontrollPositionen.size.toString(),
        kontrolle.webKontrolleAbgeschlossenAm,
        kontrolle.webKontrolleAbgeschlossenUm
    )
}

private fun mapWeWebKontrollePositionRow(kontrolle: All?, position: All, index: Int): List<String> =
    listOf(
        kontrolle?.lieferscheinnummer.orEmpty(),
        position.label.ifBlank { (index + 1).toString() },
        position.typ,
        position.nve,
        position.lcode,
        position.mhd,
        position.artikelNummer,
        position.menge
    )

private fun All.weWebKontrolleKey(): String =
    id.ifBlank { lfdNr.toString() }.ifBlank { lieferscheinnummer }

private fun All.weWebKontrolleWithStableId(): All =
    if (id.isNotBlank()) this else copy(id = weWebKontrolleKey())

private fun All.weWebKontrollePositionWithStableId(kontrolle: All?, index: Int): All =
    copy(id = "${kontrolle?.weWebKontrolleKey().orEmpty()}:${index + 1}")

private fun All.weWebKontrolleMatch(kontrolle: All): Boolean {
    val kontrolleId = kontrolle.id.ifBlank { kontrolle.lfdNr.takeIf { it > 0 }?.toString().orEmpty() }
    val lkwLfdNr = lfdNr.takeIf { it > 0 }?.toString().orEmpty()
    if (kontrolleId.isNotBlank() && (id == kontrolleId || lkwLfdNr == kontrolleId)) return true
    val ls = kontrolle.lieferscheinnummer
    return ls.isNotBlank() && lieferscheinnummer == ls
}
